/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-06 10:30:27                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-27 11:06:57                               *
 *****************************************************************************/

/* SUMMARY
 * Imports
 * Pagination: paginnation
 * previous: go to previous page
 * setPage: go to specific page
 * next: go to next page
 * isPageDisplayed: check if page selector must be displayed
 * isNotPageDisplayed: check if page selector is not displayed
 * getPagination: get page selector
 */

/* Imports */
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { t } from "../../i18n";
/***/

/**
 * Pagination: paginnation
 * Used in views that contain a table
 * Display and navigate between the table page
 * @param actualPage - page index
 * @param pageNumber - number of pages
 * @param changePage - page change event
 */
export function Pagination({
  actualPage,
  pageNumber,
  changePage,
}: {
  actualPage: number;
  pageNumber: number;
  changePage: (n: number) => void;
}) {
  const [activeIndicator, setActiveIndicator] = useState({ left: 0, width: 0 });
  const paginationRef = useRef<HTMLDivElement>(null);

  /**
   * previous: go to previous page
   */
  const previous = () => {
    changePage(actualPage - 1);
  };
  /***/

  /**
   * setPage: go to specific page
   * @param number - page to go
   */
  const setPage = (number: number) => {
    changePage(number);
  };
  /***/

  /**
   * next: go to next page
   */
  const next = () => {
    changePage(actualPage + 1);
  };
  /***/

  /**
   * isPageDisplayed: check if page selector must be displayed
   * @param i - page number to test
   * @return - page must be displayed or not
   */
  const isPageDisplayed = (i: number) => {
    if (i < 3 || i > pageNumber - 4) return true;
    if (i >= actualPage - 1 && i <= actualPage + 1) return true;

    return false;
  };
  /***/

  /**
   * isNotPageDisplayed: check if page selector is not displayed
   * @param i - page number to check
   * @return - page is not displayed
   */
  const isNotPageDisplayed = (i: number) => {
    if (isPageDisplayed(i)) return false;
    if (i === actualPage - 2 || i === actualPage + 2) return true;
    if (actualPage === 0 && i === 3) return true;
    if (actualPage + 1 === pageNumber && i === pageNumber - 4) return true;

    return false;
  };
  /***/

  // Update indicator position when active page changes
  useEffect(() => {
    const updateIndicator = () => {
      if (paginationRef.current) {
        const activeButton =
          paginationRef.current.querySelector("[data-active='true']");
        if (activeButton) {
          const rect = activeButton.getBoundingClientRect();
          const containerRect = paginationRef.current.getBoundingClientRect();

          setActiveIndicator({
            left: rect.left - containerRect.left,
            width: rect.width,
          });
        }
      }
    };

    updateIndicator();
    window.addEventListener("resize", updateIndicator);
    return () => window.removeEventListener("resize", updateIndicator);
  }, [actualPage]);

  return (
    <div className="flex flex-col items-center">
      <div className="relative py-4 px-8 w-full flex items-center justify-center">
        {/* Pagination numbers */}
        <div className="relative flex justify-center items-center">
          {/* Blue border on top */}
          <div className="absolute top-0 left-0 right-0 h-px bg-blue-100 w-full"></div>

          {/* Active indicator */}
          <div
            className="absolute top-0 h-[2px] bg-blue-500 transition-all duration-300 ease-in-out"
            style={{
              left: `${activeIndicator.left}px`,
              width: `${activeIndicator.width}px`,
            }}
          />

          {/* Numbers container */}
          <div ref={paginationRef} className="flex gap-[6px]">
            {Array.from({ length: pageNumber }).map((_, i) => {
              if (isPageDisplayed(i)) {
                return (
                  <button
                    key={`page-${i}`}
                    className={`min-w-8 h-8 px-[6px] flex items-center justify-center font-medium ${
                      i === actualPage ? "text-blue-500" : "text-gray-500"
                    }`}
                    onClick={() => setPage(i)}
                    data-active={i === actualPage}
                  >
                    {i + 1}
                  </button>
                );
              }

              if (isNotPageDisplayed(i)) {
                return (
                  <span
                    key={`ellipsis-${i}`}
                    className="min-w-8 h-8 px-[6px] flex items-center justify-center text-gray-500"
                  >
                    ...
                  </span>
                );
              }

              return null;
            })}
          </div>
        </div>

        {/* Previous button */}
        {actualPage !== 0 && (
          <button
            className="absolute left-4 text-sm text-gray-600 hover:text-blue-500 flex items-center gap-2 justify-center"
            onClick={previous}
          >
            <ArrowLeftIcon className="w-4 h-4" />
            {t("common:previous")}
          </button>
        )}

        {/* Next button */}
        {actualPage !== pageNumber - 1 && (
          <button
            className="absolute right-4 text-sm text-gray-600 hover:text-blue-500 flex items-center gap-2 justify-center"
            onClick={next}
          >
            {t("common:next")}
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
/***/
